import {
  REWARD_CARD_TYPES,
  REWARD_STATUS,
  REWARD_TYPES
} from '@/utils/constants/rewardsConstants';
import {
  computeLeftoverForQuota,
  getQuotaDecreaseError
} from '@/utils/rewardPayloadUtils';
import {
  useQuotaValidation,
  useLimitPerStoreValidation,
  useQuotaDigitalCardValidation,
  useQuotaPhysicalCardValidation,
  useLimitPerStoreDigitalCardValidation,
  useLimitPerStorePhysicalCardValidation,
  useMaxQtyPerOrderValidation
} from '../../validations';

export const useAdditionalAttributesLogic = (props) => {
  const {formData, handleFormChange, handleValidation} = props;

  const isInProgress =
    formData?.status?.toUpperCase?.() === REWARD_STATUS.ACTIVE;

  const {onChange: validateQuota} = useQuotaValidation();
  const {onChange: validateLimitPerStore} = useLimitPerStoreValidation();
  const {onChange: validateMaxQtyPerOrder} = useMaxQtyPerOrderValidation();
  const {onChange: validateQuotaDigitalCard} = useQuotaDigitalCardValidation();
  const {onChange: validateQuotaPhysicalCard} =
    useQuotaPhysicalCardValidation();
  const {onChange: validateLimitPerStoreDigitalCard} =
    useLimitPerStoreDigitalCardValidation();
  const {onChange: validateLimitPerStorePhysicalCard} =
    useLimitPerStorePhysicalCardValidation();

  const cleanQuotaFields = (isUnlimited) => {
    if (isUnlimited) {
      handleFormChange('quota', '');
      handleLeftOverInventoryChange('Unlimited');
    } else {
      handleLeftOverInventoryChange('');
    }
  };

  const cleanDigitalAndPhysicalInventoryFields = (isUnlimited) => {
    if (isUnlimited) {
      handleFormChange('quotaDigitalCard', '');
      handleFormChange('quotaPhysicalCard', '');
      handleLeftOverInventoryDigitalCardChange('Unlimited');
      handleLeftOverInventoryPhysicalCardChange('Unlimited');
    } else {
      handleLeftOverInventoryDigitalCardChange('');
      handleLeftOverInventoryPhysicalCardChange('');
    }
  };

  const handleQuotaChange = (value) => {
    const baseError = validateQuota(value);
    const decreaseError = getQuotaDecreaseError(
      value,
      formData?.originalQuota,
      isInProgress
    );
    const error = baseError ?? decreaseError ?? undefined;

    handleFormChange('quota', value);
    handleValidation('quota', error);
    handleLeftOverInventoryChange(
      computeLeftoverForQuota({
        value,
        isInProgress,
        originalQuota: formData?.originalQuota,
        originalLeftOver: formData?.originalLeftOverInventory
      })
    );
  };

  const handleLeftOverInventoryChange = (value) => {
    handleFormChange('leftOverInventory', value);
  };

  const handleLimitPerStoreChange = (value) => {
    const error = validateLimitPerStore(value);
    handleFormChange('limitPerStore', value);
    handleValidation('limitPerStore', error);
  };

  const handleMaxQtyPerOrderChange = (value) => {
    const error = validateMaxQtyPerOrder(value);
    handleFormChange('maxQtyPerOrder', value);
    handleValidation('maxQtyPerOrder', error);
  };

  const handleQuotaDigitalCardChange = (value) => {
    const baseError = validateQuotaDigitalCard(value);
    const decreaseError = getQuotaDecreaseError(
      value,
      formData?.originalQuotaDigitalCard,
      isInProgress
    );
    const error = baseError ?? decreaseError ?? undefined;

    handleFormChange('quotaDigitalCard', value);
    handleValidation('quotaDigitalCard', error);
    handleLeftOverInventoryDigitalCardChange(
      computeLeftoverForQuota({
        value,
        isInProgress,
        originalQuota: formData?.originalQuotaDigitalCard,
        originalLeftOver: formData?.originalLeftOverInventoryDigitalCard
      })
    );
  };

  const handleLeftOverInventoryDigitalCardChange = (value) => {
    handleFormChange('leftOverInventoryDigitalCard', value);
  };

  const handleLimitPerStoreDigitalCardChange = (value) => {
    const error = validateLimitPerStoreDigitalCard(value);
    handleValidation('limitPerStoreDigitalCard', error);
    handleFormChange('limitPerStoreDigitalCard', value);
  };

  const handleQuotaPhysicalCardChange = (value) => {
    const baseError = validateQuotaPhysicalCard(value);
    const decreaseError = getQuotaDecreaseError(
      value,
      formData?.originalQuotaPhysicalCard,
      isInProgress
    );
    const error = baseError ?? decreaseError ?? undefined;

    handleFormChange('quotaPhysicalCard', value);
    handleValidation('quotaPhysicalCard', error);
    handleLeftOverInventoryPhysicalCardChange(
      computeLeftoverForQuota({
        value,
        isInProgress,
        originalQuota: formData?.originalQuotaPhysicalCard,
        originalLeftOver: formData?.originalLeftOverInventoryPhysicalCard
      })
    );
  };

  const handleLeftOverInventoryPhysicalCardChange = (value) => {
    handleFormChange('leftOverInventoryPhysicalCard', value);
  };

  const handleLimitPerStorePhysicalCardChange = (value) => {
    const error = validateLimitPerStorePhysicalCard(value);
    handleValidation('limitPerStorePhysicalCard', error);
    handleFormChange('limitPerStorePhysicalCard', value);
  };

  // Handlers for ToggleRow
  const handleDisplayOutOfStockChange = (value) => {
    handleFormChange('displayOutOfStock', value);
  };

  const handleUnlimitedInventoryChange = (value) => {
    if (
      formData?.cardType === REWARD_CARD_TYPES.Both &&
      formData?.rewardType === REWARD_TYPES.VISA_CARD
    ) {
      cleanDigitalAndPhysicalInventoryFields(value);
    } else {
      cleanQuotaFields(value);
    }
    handleFormChange('unlimitedInventory', value);
  };

  const handleInStockChange = (value) => {
    handleFormChange('inStock', value);
  };

  return {
    handleQuotaChange,
    handleLeftOverInventoryChange,
    handleLimitPerStoreChange,
    handleMaxQtyPerOrderChange,
    handleQuotaDigitalCardChange,
    handleLeftOverInventoryDigitalCardChange,
    handleLimitPerStoreDigitalCardChange,
    handleQuotaPhysicalCardChange,
    handleLeftOverInventoryPhysicalCardChange,
    handleLimitPerStorePhysicalCardChange,
    handleDisplayOutOfStockChange,
    handleUnlimitedInventoryChange,
    handleInStockChange
  };
};
